// User Types
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'client';
  avatar?: string;
  companyId?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

export interface Company {
  id: string;
  name: string;
  cnpj: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  planId: string;
  status: 'active' | 'inactive' | 'suspended';
  createdAt: string;
  expiresAt: string;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  maxUsers: number;
  features: string[];
  isPopular?: boolean;
}

export interface Subscription {
  id: string;
  companyId: string;
  planId: string;
  billingCycle: 'monthly' | 'yearly';
  status: 'active' | 'canceled' | 'expired' | 'pending';
  startDate: string;
  endDate: string;
  nextBillingDate: string;
  amount: number;
}

export interface Invoice {
  id: string;
  companyId: string;
  subscriptionId: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'canceled';
  dueDate: string;
  paidDate?: string;
  invoiceNumber: string;
  description: string;
}

export interface Payment {
  id: string;
  companyId: string;
  invoiceId: string;
  amount: number;
  method: 'credit_card' | 'boleto' | 'pix';
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
  completedAt?: string;
}

export interface Ticket {
  id: string;
  companyId: string;
  userId: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthState {
  user: User | null;
  company: Company | null;
  subscription: Subscription | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  companyName: string;
  cnpj: string;
  email: string;
  phone: string;
  adminName: string;
  adminEmail: string;
  password: string;
}

export interface PaymentData {
  cardNumber: string;
  cardHolder: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  cpf: string;
}

// Mock Data
export const MOCK_PLANS: Plan[] = [
  {
    id: 'plan-1',
    name: 'Básico',
    description: 'Para empresas de até 100 funcionários',
    monthlyPrice: 229.90,
    yearlyPrice: 2299,
    maxUsers: 100,
    features: [
      'Canal personalizável com a identidade da sua marca',
      'Conformidade com a NR-1',
      'Suporte por e-mail (48h)',
      'Dashboard (Simplificado)',
      'SLA de resposta (Padrão)',
      '1 acesso da equipe ao painel administrativo'
    ]
  },
  {
    id: 'plan-2',
    name: 'Premium',
    description: 'Para empresas de 101 a 200 funcionários',
    monthlyPrice: 449.90,
    yearlyPrice: 4499,
    maxUsers: 200,
    isPopular: true,
    features: [
      'Tudo do Básico',
      'Acesso ao Portal Ethics de Treinamentos',
      'Opção de 3 treinamentos de Compliance no portal',
      'Certificado dos Treinamentos',
      'Dashboards (Avançados)',
      'Suporte por e-mail e chat (24H)',
      'SLA de resposta (Prioritário)',
      '2 acessos da equipe ao painel administrativo'
    ]
  },
  {
    id: 'plan-3',
    name: 'Personalizado',
    description: 'Para empresas com mais de 201 funcionários',
    monthlyPrice: 0,
    yearlyPrice: 0,
    maxUsers: 999999,
    features: [
      'Tudo do Premium',
      'Treinamento da equipe',
      'Opção de 10 treinamentos da plataforma',
      'Canal de suporte exclusivo com responsável dedicado',
      'Suporte prioritário e exclusivo (Gerente dedicado)',
      '5+ acessos da equipe ao painel administrativo'
    ]
  }
];

export const MOCK_USERS: User[] = [
  {
    id: 'user-1',
    name: 'Admin Portal',
    email: 'admin@portalethics.com',
    role: 'admin',
    status: 'active',
    createdAt: '2024-01-01'
  },
  {
    id: 'user-2',
    name: 'Maria Silva',
    email: 'maria@empresa.com',
    role: 'client',
    companyId: 'comp-1',
    status: 'active',
    createdAt: '2024-01-15'
  },
  {
    id: 'user-3',
    name: 'João Santos',
    email: 'joao@empresa.com',
    role: 'client',
    companyId: 'comp-1',
    status: 'active',
    createdAt: '2024-01-20'
  }
];

export const MOCK_COMPANIES: Company[] = [
  {
    id: 'comp-1',
    name: 'Empresa Exemplo Ltda',
    cnpj: '12.345.678/0001-90',
    email: 'contato@empresa.com',
    phone: '(11) 1234-5678',
    address: 'Rua Exemplo, 123',
    city: 'São Paulo',
    state: 'SP',
    zipCode: '01001-000',
    planId: 'plan-2',
    status: 'active',
    createdAt: '2024-01-15',
    expiresAt: '2025-01-15'
  }
];

export const MOCK_SUBSCRIPTIONS: Subscription[] = [
  {
    id: 'sub-1',
    companyId: 'comp-1',
    planId: 'plan-2',
    billingCycle: 'yearly',
    status: 'active',
    startDate: '2024-01-15',
    endDate: '2025-01-15',
    nextBillingDate: '2025-01-15',
    amount: 4970
  }
];

export const MOCK_INVOICES: Invoice[] = [
  {
    id: 'inv-1',
    companyId: 'comp-1',
    subscriptionId: 'sub-1',
    amount: 4970,
    status: 'paid',
    dueDate: '2024-01-15',
    paidDate: '2024-01-15',
    invoiceNumber: 'INV-2024-0001',
    description: 'Assinatura Profissional - Anual'
  }
];

export const MOCK_TICKETS: Ticket[] = [
  {
    id: 'ticket-1',
    companyId: 'comp-1',
    userId: 'user-2',
    title: 'Dúvida sobre configuração',
    description: 'Como configuro o canal de denúncias?',
    status: 'resolved',
    priority: 'medium',
    category: 'Configuração',
    createdAt: '2024-01-20',
    updatedAt: '2024-01-21'
  }
];
